import type { SerializedExcalidrawElement } from "./domain.js";

/**
 * Presentation lessons (발표 수업, plan 2026-10-28). A student's canvas holds
 * slides: frames laid left to right on one row, one slot each. Position is
 * the order — slot k is slide k+1 — so what the student sees on the canvas
 * and what the slideshow plays never disagree.
 */

/** Slides one student canvas may hold. */
export const MAX_PRESENTATION_FRAMES = 30;

/** A slide's size: the same A4-landscape frame as the class templates. */
export const PRESENTATION_FRAME_WIDTH = 840;
export const PRESENTATION_FRAME_HEIGHT = 594;
/** Gap between neighbouring slides on the row. */
export const PRESENTATION_FRAME_GAP = 120;

/** The id prefix of a canvas's first slide, the one its editor lays on an
 * empty canvas (`presentation-first-<canvasId>`, then `-2`, `-3` … should an
 * older build have let it be deleted). */
const FIRST_SLIDE_PREFIX = "presentation-first-";

/** The first slide's id, the same in every editor of one canvas. */
export const presentationFirstSlideId = (canvasId: string): string =>
  `${FIRST_SLIDE_PREFIX}${canvasId}`;

/** Whether `element` is a first slide, which nobody deletes: losing every
 * slide left two students with nowhere to add one (10-30). */
export const isPresentationFirstSlide = (
  element: SerializedExcalidrawElement,
): boolean =>
  (element.type === "frame" || element.type === "magicframe") &&
  element.id.startsWith(FIRST_SLIDE_PREFIX);

/** Top-left corner of slot `index` (0-based). */
export const presentationSlotAt = (index: number): { x: number; y: number } => ({
  x: index * (PRESENTATION_FRAME_WIDTH + PRESENTATION_FRAME_GAP),
  y: 0,
});

/** The live frames of a student canvas in slide order: left to right, then
 * top to bottom should a stale copy ever leave one off the row. */
export const presentationSlides = <T extends SerializedExcalidrawElement>(
  scene: readonly T[],
): T[] =>
  scene
    .filter(
      (element) =>
        (element.type === "frame" || element.type === "magicframe") &&
        !element.isDeleted,
    )
    .sort(
      (a, b) =>
        Number(a.x ?? 0) - Number(b.x ?? 0) ||
        Number(a.y ?? 0) - Number(b.y ?? 0),
    );

/** How many slides a scene holds; what the teacher's card shows. */
export const presentationSlideCount = (
  scene: readonly SerializedExcalidrawElement[],
): number => presentationSlides(scene).length;
